/**
 * One workflow, from picking a context to handing a corpus over.
 *
 * The product had a page per capability and a different progress metaphor on
 * each: a step spine on the bucket page, step cards inside packaging, a list of
 * past runs on the company page. An operator could be three clicks deep and
 * still not know which stage they were in, what finished it, or what came next.
 * Past runs were the most prominent thing on screen, which is backwards -
 * history is what you consult, not what you do.
 *
 * So the journey is modelled once, here, as six stages with a single current
 * position and a single next action. Every workflow surface renders the same
 * spine from the same facts, and a stage that cannot complete today says so at
 * the point an operator would otherwise try.
 *
 * Pure and tested: the honesty of the whole app rests on this saying "waiting"
 * when a read has not landed rather than inventing a position.
 */

export type StageId = 'context' | 'observe' | 'attribute' | 'review' | 'package' | 'deliver';

export type StageStatus =
  /** Finished, and its output exists. */
  | 'done'
  /** Where the operator is now, and where the action lives. */
  | 'current'
  /** Reachable, but not yet started. */
  | 'ahead'
  /** Running on the backend; nothing for the operator to do. */
  | 'running'
  /** Reached, and cannot complete with what the backend does today. */
  | 'blocked'
  /**
   * Ran and failed.
   *
   * The spine had no way to say this, so a failed delivery was reported as
   * blocked, which reads as "waiting on something" rather than "this went
   * wrong", and the bucket page invented a fourth state of its own to cover
   * the gap.
   */
  | 'failed'
  /** Not yet known, because a read has not come back. */
  | 'unknown';

export interface Stage {
  id: StageId;
  /** What the stage is, in the operator's words. */
  title: string;
  /** What finishing it means. */
  done: string;
  status: StageStatus;
}

export interface WorkflowFacts {
  /** A company and a bucket have been chosen. */
  hasContext: boolean;
  /** Every read this depends on has come back. */
  known: boolean;
  /** A listing has completed at least once. */
  observed: boolean;
  /** A listing is running now. */
  observing: boolean;
  /** The attribution walk has sealed. */
  sealed: boolean;
  /** The walk is running now. */
  walking: boolean;
  /** Tools the sealed walk attributed. */
  toolCount: number;
  /**
   * Objects the seal actually accounted for, and objects the listing found.
   *
   * A seal is a claim about a population, and it can be made over an empty
   * one. aiinsurance26 listed 34,244 objects and then sealed with
   * `inventoryObjectCount: 0`, `toolObjectCount: 0`, `strayObjectCount: 0` and
   * no entries - a walk that ran, finished, and covered nothing. Every stage
   * after it inherited that: the population registered 0 records, the manifest
   * came back empty, and the gate refused the package. Meanwhile the structure
   * tree drew 34,244 files from a different derivation, so the screen showed
   * tens of thousands of files above a package that could never contain one.
   *
   * `sealed` alone cannot tell those apart, so the stage read done and the
   * operator was sent forward to build a package out of nothing. Null when the
   * number is not known, which is not zero.
   */
  sealedObjects: number | null;
  listedObjects: number | null;
  /**
   * Detections that hold enough evidence to be confirmed but have not been.
   *
   * A placement rule keys on a supported detection, so nothing can be placed
   * until someone attests to one. Packaging a bucket whose detections are all
   * still candidates produces a package with no files in it, correctly and
   * uselessly. This is the fact that lets the product say so first.
   */
  /**
   * Null when the detections read has not answered, which is not the same as
   * zero. The company-tools route answers 500 in production today, and a page
   * that reads that as "nothing is confirmed" sends an operator to walk a
   * bucket whose walk already sealed.
   */
  promotable: number | null;
  /** Detections already confirmed, or null when the read did not answer. */
  supported: number | null;
  /** A packaging run exists, and its state, or null when none was started. */
  packagingState: string | null;
  /** Exports have been built and can be downloaded. */
  exportsBuilt: boolean;
  /**
   * Whether the run has a registered population, or null before the gate has
   * been read. Null is not false: an unknown population must never be reported
   * as an empty one, because the advice differs completely.
   */
  populationRegistered: boolean | null;
  /**
   * The delivery's own state, when one exists.
   *
   * The run and its delivery are separate records and 0078 is deliberately
   * tolerant about keeping them in step: a run whose gate cannot be freshly
   * re-sealed after passage stays at GATE_PASSED while its delivery proceeds
   * and completes. That is the run telling the truth about itself rather than
   * being handed manufactured evidence, and it means the run state alone is
   * not the authority on whether a handover happened.
   */
  deliveryState: string | null;
}

export interface NextAction {
  /** The single thing to do now. */
  label: string;
  /** Where it happens. */
  to: string;
  /** Why this and not something else. */
  because: string;
}

/**
 * Packaging states that still need a decision from the operator.
 *
 * STRUCTURING is deliberately not "name it". Naming leaves that state for
 * good and it is the only state that accepts a population, so pointing an
 * operator at naming is pointing them at the one move that strands the run.
 * It resolves through `packagingOpen` instead, which knows whether the
 * population exists.
 */
const PACKAGING_OPEN: Record<string, string> = {
  DISCOVERING: 'choose what ships',
  AXIS_PENDING: 'choose the layout',
  STRUCTURING: 'name it',
  NAMING: 'name it',
  DOCUMENTING: 'approve its documents',
  EXPORTABLE: 'build and download the files',
  GATE_PENDING: 'hand it over',
  GATE_PASSED: 'hand it over',
  GATE_FAILED: 'start a new run',
  DELIVERY_FAILED: 'start a new run',
};

/**
 * States a run passes through BEFORE the population window opens.
 *
 * The population is registered while a run is structuring. Everything here
 * comes first, so a run sitting in one of them has not missed anything - the
 * window is still ahead of it.
 */
const BEFORE_POPULATION = ['DISCOVERING', 'AXIS_PENDING'];

/** Whether the run has gone past the only state that accepts a population. */
function pastPopulationWindow(f: WorkflowFacts): boolean {
  const state = f.packagingState;
  if (!state) return false;
  return f.populationRegistered === false && state !== 'STRUCTURING' && !BEFORE_POPULATION.includes(state);
}

/** What a state still needs, once the population is taken into account. */
export function packagingOpen(f: WorkflowFacts): string | null {
  const state = f.packagingState;
  if (!state) return null;
  if (f.populationRegistered === false && !BEFORE_POPULATION.includes(state)) {
    // Inside structuring the run is still recoverable, and this is the move
    // that recovers it.
    if (state === 'STRUCTURING') return 'take stock of every file';
    // Past it, the run holds nothing and never can. Every remaining step
    // completes and the gate still refuses at the end, so naming, approving
    // and exporting are all work that leads nowhere. stopReason speaks here
    // instead of a pointer walking someone further down a dead path.
    return null;
  }
  return PACKAGING_OPEN[state] ?? null;
}

/** The stages in order, for anything that needs to show the shape of the work. */
export const STAGE_ORDER: StageId[] = ['context', 'observe', 'attribute', 'review', 'package', 'deliver'];

export const TITLES: Record<StageId, { title: string; done: string }> = {
  context: { title: 'Choose the data', done: 'a company and a bucket are selected' },
  observe: { title: 'List what is there', done: 'every file has been seen once' },
  attribute: { title: 'Trace it to software', done: 'the walk sealed and named the tools' },
  review: { title: 'Check what was found', done: 'you have read the counts and the checks' },
  package: { title: 'Build the package', done: 'documents approved and files downloadable' },
  deliver: { title: 'Hand it over', done: 'copied into the buyer’s own bucket' },
};

/**
 * The six stages with their current status.
 *
 * Deliver used to be hardcoded blocked, on the grounds that no worker
 * performed one. A worker exists and a sweep claims queued deliveries, so that
 * became a stale claim the rail repeated on every screen. It is now derived
 * like every other stage: reachable once the gate is reached, and genuinely
 * blocked only when the package it would carry is empty.
 */
export function stagesFor(f: WorkflowFacts): Stage[] {
  const at = (id: StageId, status: StageStatus): Stage => ({ id, ...TITLES[id], status });
  const deliverStatus = (): StageStatus => {
    if (!f.known) return 'unknown';
    // The delivery record wins where the two disagree. A completed handover
    // drawn as "ready to hand over" because the run lagged is the rail calling
    // a finished thing unstarted.
    if (f.deliveryState === 'DELIVERED') return 'done';
    if (f.deliveryState === 'FAILED' || f.deliveryState === 'DELIVERY_FAILED') return 'failed';
    // Queued, creating the bucket, uploading, verifying: all in flight.
    if (f.deliveryState) return 'running';
    const state = f.packagingState;
    if (state === 'DELIVERED') return 'done';
    if (state === 'DELIVERING') return 'running';
    if (state === 'DELIVERY_FAILED') return 'failed';
    // A delivery carries the manifest. With no manifest, this cannot complete
    // however many times it is pressed, which is a block rather than a step.
    if (f.populationRegistered === false) return 'blocked';
    if (state === 'GATE_PENDING' || state === 'GATE_PASSED') return 'current';
    return 'ahead';
  };

  if (!f.hasContext) {
    return [
      at('context', 'current'),
      at('observe', 'ahead'),
      at('attribute', 'ahead'),
      at('review', 'ahead'),
      at('package', 'ahead'),
      at('deliver', 'ahead'),
    ];
  }

  if (!f.known) {
    return [
      at('context', 'done'),
      at('observe', 'unknown'),
      at('attribute', 'unknown'),
      at('review', 'unknown'),
      at('package', 'unknown'),
      at('deliver', 'unknown'),
    ];
  }

  const observe: StageStatus = f.observed ? 'done' : f.observing ? 'running' : 'current';
  /**
   * A seal over nothing is a failed walk wearing a finished walk's clothes.
   * Only claimed when both numbers are known: unknown is not zero.
   */
  const sealedNothing =
    f.sealed && f.sealedObjects === 0 && f.listedObjects !== null && f.listedObjects > 0;

  const attribute: StageStatus = sealedNothing
    ? 'failed'
    : f.sealed
      ? 'done'
      : f.walking
        ? 'running'
        : f.observed
          ? 'current'
          : 'ahead';

  // Review is finished the moment a package is started: choosing to package is
  // the act of accepting what the walk found.
  const started = f.packagingState !== null;
  const review: StageStatus = !f.sealed || sealedNothing ? 'ahead' : started ? 'done' : 'current';

  /**
   * Reaching the gate finishes building. The open-state test used to run
   * first, and GATE_PENDING and GATE_PASSED are both open states - their move
   * is "hand it over" - so the package drew itself current at the same moment
   * delivery did. Two stages current at once, on the rail, in production, for
   * 416 of the fact combinations the tests now enumerate: whichever of them a
   * reader believed, the other contradicted it.
   *
   * A package that has been handed to a delivery is finished being built too,
   * however that delivery ended.
   */
  const state = f.packagingState;
  const built =
    f.exportsBuilt ||
    state === 'GATE_PENDING' ||
    state === 'GATE_PASSED' ||
    state === 'DELIVERING' ||
    state === 'DELIVERED' ||
    state === 'DELIVERY_FAILED';
  const pkg: StageStatus = !f.sealed
    ? 'ahead'
    : !started
      ? 'ahead'
      : built
        ? 'done'
        : state && PACKAGING_OPEN[state]
          ? 'current'
          : 'running';

  return oneCurrent([
    at('context', 'done'),
    at('observe', observe),
    at('attribute', attribute),
    at('review', review),
    at('package', pkg),
    at('deliver', deliverStatus()),
  ]);
}

/**
 * The position is a single place, and each stage decided its own status from
 * the facts it cared about.
 *
 * So a bucket whose listing had not completed but whose packaging record sat
 * at the gate - which the backend can and does report, since the two records
 * are kept deliberately independent - drew "List what is there" and "Hand it
 * over" as current at the same time. Four such pairs existed. A later stage
 * cannot be where an operator is while an earlier one is still unfinished:
 * whatever the later record says about itself, the work in front of it is not
 * reachable yet.
 *
 * Only `current` is rewritten. Running, blocked and failed are facts about a
 * record that exists, and demoting those would hide something that happened.
 */
function oneCurrent(stages: Stage[]): Stage[] {
  let taken = false;
  return stages.map((s) => {
    if (s.status !== 'current') return s;
    if (taken) return { ...s, status: 'ahead' as const };
    taken = true;
    return s;
  });
}

/** The stage an operator is in: the first that is not finished. */
export function currentStage(stages: Stage[]): Stage {
  return (
    stages.find((s) => s.status === 'current') ??
    stages.find((s) => s.status === 'running') ??
    stages.find((s) => s.status === 'unknown') ??
    stages.find((s) => s.status !== 'done') ??
    stages[stages.length - 1]!
  );
}

/**
 * The one action to take now, or null when the answer is genuinely to wait.
 *
 * Never more than one: an operator asking "what do I do" is not helped by
 * three buttons of equal weight.
 */
export function nextAction(f: WorkflowFacts, bucketId: string | null): NextAction | null {
  if (!f.hasContext || !bucketId) {
    return { label: 'Choose a bucket', to: '/', because: 'Everything else is scoped to one bucket.' };
  }
  if (!f.known) return null;
  if (f.observing || f.walking) return null;

  if (!f.observed) {
    return {
      label: 'List this bucket',
      to: `/buckets/${bucketId}`,
      because: 'Nothing can be traced before its files have been seen once.',
    };
  }
  /**
   * A seal that covered nothing outranks everything after it. The stages below
   * all key off `sealed`, so without this the product walked an operator into
   * naming and packaging a run that could never hold a file.
   */
  if (f.sealed && f.sealedObjects === 0 && f.listedObjects !== null && f.listedObjects > 0) {
    return {
      label: 'Walk this bucket again',
      to: `/buckets/${bucketId}#walk`,
      because: `The walk sealed without covering a single object, while the listing found ${f.listedObjects.toLocaleString()}. Nothing was traced, so there is nothing to confirm and a package built from it would be empty.`,
    };
  }
  if (!f.sealed) {
    return {
      label: 'Walk this bucket',
      to: `/buckets/${bucketId}`,
      because: 'The walk reads every file and records which software made it.',
    };
  }
  const open = packagingOpen(f);
  if (open) {
    /**
     * Why this move, and not another.
     *
     * A run whose gate refused, or whose handover failed, was told that "a
     * package is part-built and waiting on this decision" - the sentence for a
     * healthy run mid-ladder. The move it names is "start a new run", so the
     * reason has to be the thing that ended this one.
     */
    const because =
      f.packagingState === 'STRUCTURING' && f.populationRegistered === false
        ? 'Nothing is in this package yet, and structuring is the only state that accepts it. Naming it now would close that window for good.'
        : f.packagingState === 'GATE_FAILED'
          ? 'This run’s release checks refused it, and a refused run does not advance. Its decisions stay on the record.'
          : f.packagingState === 'DELIVERY_FAILED' || f.deliveryState === 'FAILED' || f.deliveryState === 'DELIVERY_FAILED'
            ? 'The handover failed, and a delivery is not retried on the same run. The package page carries the recorded cause.'
            : 'A package is part-built and waiting on this decision.';
    return { label: `Continue the package: ${open}`, to: `/buckets/${bucketId}/package`, because };
  }
  if (!f.packagingState) {
    // Packaging before anything is confirmed builds an empty package: every
    // disposition comes back UNASSIGNED because no rule has a supported
    // detection to key on. Confirming is the move that changes the outcome.
    // Nothing below may be claimed while the detections are unknown: every
    // branch here turns on a count, and a failed read has no count.
    if (f.supported === null || f.promotable === null) {
      return {
        label: 'Read the detections again',
        to: `/buckets/${bucketId}#confirm-tools`,
        because:
          'The detections for this scope could not be read, so what is confirmed and what is short of the bar are both unknown. Nothing here is a count until that answers.',
      };
    }
    if (f.supported === 0 && f.promotable > 0) {
      return {
        label: `Confirm ${f.promotable} ${f.promotable === 1 ? 'tool' : 'tools'}`,
        // Anchored: on the bucket page itself this button pointed at the URL
        // already loaded, so the one thing the banner told an operator to do
        // did nothing when they pressed it.
        to: `/buckets/${bucketId}#confirm-tools`,
        because:
          'Nothing here is confirmed yet, and only a confirmed tool can have its files placed. Packaging first would build a package with nothing in it.',
      };
    }
    if (f.supported === 0) {
      return {
        label: 'Walk this bucket again',
        to: `/buckets/${bucketId}#walk`,
        because:
          'No detection holds enough evidence to be confirmed, so nothing can be placed in a package yet. More evidence comes from another walk.',
      };
    }
    return {
      label: 'Package this bucket',
      to: `/buckets/${bucketId}/package`,
      because: `${f.supported} confirmed ${f.supported === 1 ? 'tool' : 'tools'}. Packaging is the path that ends in files you can hand over.`,
    };
  }
  return null;
}

/** Why the workflow stops where it does, when it stops. */
export function stopReason(f: WorkflowFacts): string | null {
  if (!f.known) return null;
  /**
   * The state that has no way forward: past structuring with nothing in the
   * package. Every control still renders and none of them can change it.
   *
   * The test used to be "not structuring", which is also true of the two
   * states a run passes through on its way TO structuring. So a run at
   * "choose the layout" - healthy, two decisions old, with its population
   * window still ahead of it - was told that nothing on the page could
   * complete it and that its only way forward was to start again. The most
   * discouraging sentence in the product, shown to a run with nothing wrong
   * with it, and the banner dropped its action to say so.
   */
  if (pastPopulationWindow(f)) {
    return 'This run holds no files and has moved past the only state that could give it any, so nothing on this page can complete it. A new run over the same bucket is the way forward, and the decisions made here are recorded rather than lost.';
  }
  return null;
}
